import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Box, CircularProgress, Typography, Select, MenuItem, FormControl, } from '@weave-mui/material';
import { circularProgressSize } from '@weave-mui/enums';
import { useAnalyticsTrends } from '../../api/hooks';
import { getErrorDetail } from '../../utils/errorDetail';
import BoardHeader from '../BoardHeader/BoardHeader';
import TrendChart from './TrendChart';
const EMPTY_SERIES = [];
const MS_PER_MINUTE = 60000;
const RANGE_OPTIONS = [
    { value: '24h', label: 'Last 24 Hours' },
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
];
function ChartGrid({ charts }) {
    return (_jsx(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }, children: charts.map(({ key, title, valueLabel, query, mode, unitDivisor }) => {
            if (query.isLoading) {
                return (_jsx(Box, { sx: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 320, border: '1px solid #dfe1e6', borderRadius: 2 }, children: _jsx(CircularProgress, { size: circularProgressSize.S }) }, key));
            }
            if (query.error) {
                return (_jsx(Box, { sx: { minHeight: 320, border: '1px solid #dfe1e6', borderRadius: 2, p: 2 }, children: _jsxs(Typography, { color: "error", children: ["Failed to load ", title.toLowerCase(), ": ", getErrorDetail(query.error)] }) }, key));
            }
            const series = query.data?.series ?? EMPTY_SERIES;
            const truncated = series.some((s) => s.truncated);
            return (_jsxs(Box, { children: [_jsx(TrendChart, { title: title, series: series, valueLabel: valueLabel, mode: mode, unitDivisor: unitDivisor }), truncated && (_jsx(Typography, { variant: "caption", color: "text.secondary", children: "Some data may be incomplete for this period \u2014 showing a partial result." }))] }, key));
        }) }));
}
function Section({ title, charts }) {
    return (_jsxs(Box, { sx: { mb: 4 }, children: [_jsx(Typography, { component: "h3", sx: { fontFamily: '"DM Sans", sans-serif', fontWeight: 600, fontSize: '0.85rem', color: '#5e6c84', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1.5 }, children: title }), _jsx(ChartGrid, { charts: charts })] }));
}
export default function AnalyticsBoard() {
    const [range, setRange] = useState('24h');
    // 30d only supports bucket=day (AnalyticsService performance cap); other ranges keep
    // volume's hour-level detail.
    const volumeBucket = range === '30d' ? 'day' : 'hour';
    // 6 concurrent queries here, above this repo's general ≤5 external-call cap (AGENTS.md) —
    // that guideline targets a backend loop calling a third-party API; this is the browser
    // calling its own backend for an analytics dashboard, each query independently cacheable
    // by React Query, so the cap doesn't apply the same way. Total Tasks/Total Cost below reuse
    // the volume/cost queries instead of adding 2 more, keeping this at 6 rather than 8.
    const volume = useAnalyticsTrends({ metric: 'volume', range, bucket: volumeBucket });
    const successRate = useAnalyticsTrends({ metric: 'successRate', range, bucket: 'day' });
    const duration = useAnalyticsTrends({ metric: 'duration', range });
    const durationPerTask = useAnalyticsTrends({ metric: 'durationPerTask', range });
    const cost = useAnalyticsTrends({ metric: 'cost', range });
    const costPerTask = useAnalyticsTrends({ metric: 'costPerTask', range });
    // Total Tasks / Total Cost are running sums of the volume/cost trend data already fetched
    // above for the "Task Volume" / "Cost" charts — computed client-side (TrendChart's
    // mode="cumulative") rather than as their own metric, so they cost zero extra API calls.
    const sections = [
        {
            title: 'Tasks',
            charts: [
                { key: 'volume', title: 'Task Volume', valueLabel: 'Tasks', query: volume },
                { key: 'totalTasks', title: 'Total Tasks', valueLabel: 'Tasks (cumulative)', query: volume, mode: 'cumulative' },
            ],
        },
        {
            title: 'Success Rate & Duration',
            charts: [
                { key: 'successRate', title: 'Success Rate', valueLabel: 'Rate', query: successRate },
                { key: 'duration', title: 'Duration', valueLabel: 'min', query: duration, unitDivisor: MS_PER_MINUTE },
                {
                    key: 'durationPerTask', title: 'Duration per Task', valueLabel: 'min', query: durationPerTask, mode: 'percentile', unitDivisor: MS_PER_MINUTE,
                },
            ],
        },
        {
            title: 'Costs',
            charts: [
                { key: 'cost', title: 'Cost', valueLabel: 'USD', query: cost },
                { key: 'costPerTask', title: 'Cost per Task', valueLabel: 'USD', query: costPerTask, mode: 'percentile' },
                { key: 'totalCost', title: 'Total Cost', valueLabel: 'USD (cumulative)', query: cost, mode: 'cumulative' },
            ],
        },
    ];
    return (_jsxs(Box, { children: [_jsx(BoardHeader, { title: "Analytics", children: _jsx(FormControl, { size: "small", sx: { minWidth: 140 }, children: _jsx(Select, { value: range, "aria-label": "Analytics time range", onChange: (e) => setRange(e.target.value), sx: {
                            fontFamily: '"DM Sans", sans-serif',
                            fontSize: '0.8rem',
                            borderRadius: '3px',
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#dfe1e6' },
                        }, children: RANGE_OPTIONS.map(({ value, label }) => (_jsx(MenuItem, { value: value, sx: { fontFamily: '"DM Sans", sans-serif', fontSize: '0.8rem' }, children: label }, value))) }) }) }), sections.map((section) => (_jsx(Section, { title: section.title, charts: section.charts }, section.title)))] }));
}
